"use client";

import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import Typography, { type TypographyVariant } from "src/components/common/Typography/Typography";
import { IconHash } from "src/Icons";
import { useExtracted } from "#i18n";
import styles from "./DocHeading.module.scss";

export interface DocHeadingProps {
  level: 1 | 2 | 3 | 4 | 5 | 6;
  id?: string;
  children?: React.ReactNode;
  className?: string;
}

const DocHeading = (props: DocHeadingProps) => {
  const translateMessage = useExtracted();
  const { level, id, children, className } = props;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [copied, setCopied] = useState(false);

  const Tag = `h${level}` as const;
  const variant: TypographyVariant =
    level === 1 ? "heading1" : level === 2 ? "heading2" : level === 3 ? "heading3" : "heading4";

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (!id) return;

    // Update URL without scrolling
    window.history.pushState(null, "", `#${id}`);

    // Copy link to clipboard
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });

    // Scroll to element
    headingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    // Scroll to heading if URL hash matches on mount
    if (id && window.location.hash === `#${id}`) {
      setTimeout(() => {
        headingRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [id]);

  return (
    <Typography
      as={Tag}
      variant={variant}
      ref={headingRef}
      id={id}
      className={clsx(styles.heading, className)}
    >
      {children}
      {id && (
        <a
          href={`#${id}`}
          onClick={handleAnchorClick}
          className={styles.anchor}
          aria-label={`Link to ${children}`}
          title={
            copied ? translateMessage("Link copied!") : translateMessage("Copy link to section")
          }
        >
          <IconHash />
        </a>
      )}
    </Typography>
  );
};

export default DocHeading;

// Export heading components for MDX
export const H1 = (props: Omit<React.ComponentPropsWithoutRef<"h1">, "level">) => (
  <DocHeading level={1} {...props} />
);

export const H2 = (props: Omit<React.ComponentPropsWithoutRef<"h2">, "level">) => (
  <DocHeading level={2} {...props} />
);

export const H3 = (props: Omit<React.ComponentPropsWithoutRef<"h3">, "level">) => (
  <DocHeading level={3} {...props} />
);

export const H4 = (props: Omit<React.ComponentPropsWithoutRef<"h4">, "level">) => (
  <DocHeading level={4} {...props} />
);

export const H5 = (props: Omit<React.ComponentPropsWithoutRef<"h5">, "level">) => (
  <DocHeading level={5} {...props} />
);

export const H6 = (props: Omit<React.ComponentPropsWithoutRef<"h6">, "level">) => (
  <DocHeading level={6} {...props} />
);
